import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

/**
 * Telegraphed Charge — a Dash-Slam variant built to be *unmistakable*: a long
 * wind-up and a bold, arena-spanning lane preview (a thick beam along the exact
 * path the boss will take) instead of Dash Slam's thin dashed lock-on line. The
 * lane locks in at telegraph start and flashes "imminent" just before the boss
 * commits, so a hit is always the player's read, never a surprise.
 *
 * Drives the boss's own position, so it sets `boss.locked` for the duration
 * (see Boss.update's idle-wander suspend). `config` (speed/contactDamage/width)
 * is per-boss tuning, supplied by data/bosses.js.
 */
export class TelegraphedCharge extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Telegraphed Charge', telegraphDuration: 1.2, activeDuration: 0.45, minPhase });
    this.config = config;
  }

  onBegin(boss, player) {
    boss.locked = true;
    this._angle = Math.atan2(player.y - boss.y, player.x - boss.x);
    this._dirX = Math.cos(this._angle);
    this._dirY = Math.sin(this._angle);
    // Lane long enough to cross the whole arena from the boss.
    this._length = Math.hypot(ARENA.width, ARENA.height);
  }

  onActiveStart() {
    this._hasHitPlayer = false;
  }

  onActiveTick(dt, boss, player) {
    const speed = this.config.speed + boss.phase * 30;
    boss.x += this._dirX * speed * dt;
    boss.y += this._dirY * speed * dt;
    this._clampBossToArena(boss);

    if (!this._hasHitPlayer) {
      const dist = Math.hypot(player.x - boss.x, player.y - boss.y);
      if (dist < boss.radius + player.radius) {
        player.takeDamage(this.config.contactDamage);
        this._hasHitPlayer = true;
      }
    }
  }

  onEnd(boss) {
    boss.locked = false;
  }

  _clampBossToArena(boss) {
    boss.x = Math.min(ARENA.x + ARENA.width - boss.radius, Math.max(ARENA.x + boss.radius, boss.x));
    boss.y = Math.min(ARENA.y + ARENA.height - boss.radius, Math.max(ARENA.y + boss.radius, boss.y));
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      // Flash "imminent" (bright/solid) in the last third of the wind-up.
      const imminent = this._timer <= this.telegraphDuration / 3;
      return { kind: 'chargeLane', x: boss.x, y: boss.y, angle: this._angle, length: this._length, width: this.config.width, imminent };
    }
    if (this.state === PatternState.ACTIVE) {
      return { kind: 'chargeLane', x: boss.x, y: boss.y, angle: this._angle, length: this._length, width: this.config.width, imminent: true };
    }
    return null;
  }
}
